import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Reveal } from './Reveal';
import { PRICING } from './content';

type Period = 'monthly' | 'annual';

const PERIODS: { id: Period; label: string }[] = [
  { id: 'monthly', label: 'Monthly' },
  { id: 'annual', label: 'Annual' },
];

export function Pricing() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('annual');

  const annualSavings = PRICING.find((t) => t.annual)?.annual?.savings;

  return (
    <section id="pricing" className="scroll-mt-16 border-b border-hairline py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center sm:mb-10">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Simple, transparent pricing
          </h2>
          <p className="mt-3 text-base text-muted-foreground">
            Start free. Upgrade when your team needs more.
          </p>
        </div>

        {/* Billing period toggle — Monthly / Annual (annual = 2 months free) */}
        <div className="mb-12 flex items-center justify-center gap-3 sm:mb-14">
          <div
            className="inline-flex rounded-lg border border-hairline bg-card/40 p-1 text-sm"
            role="tablist"
            aria-label="Billing period"
          >
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={period === p.id}
                onClick={() => setPeriod(p.id)}
                className={cn(
                  'rounded-md px-4 py-1.5 font-medium transition-colors duration-150 ease-out-quart',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  period === p.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          {annualSavings && (
            <span className="rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
              {annualSavings}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {PRICING.map((tier, i) => {
            const annual = period === 'annual' ? tier.annual : undefined;
            const price = annual ? annual.price : tier.price;
            return (
              <Reveal key={tier.name} delay={i * 0.08} className="h-full">
                <div
                  className={cn(
                    'glass-card flex h-full flex-col rounded-xl',
                    tier.highlighted && 'glow-border',
                  )}
                >
                  <div className="flex flex-1 flex-col gap-5 px-6 py-7">
                    <div className="space-y-2">
                      {tier.badge && (
                        <Badge variant="default" className="self-start">
                          {tier.badge}
                        </Badge>
                      )}
                      <h3 className="text-lg font-semibold tracking-tight">
                        {tier.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {tier.blurb}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-mono text-4xl font-semibold tabular-nums tracking-tight">
                          {price}
                        </span>
                        {tier.cadence && (
                          <span className="text-sm text-muted-foreground">
                            {tier.cadence}
                          </span>
                        )}
                      </div>
                      {annual && (
                        <p className="mt-1.5 text-xs text-muted-foreground">
                          {annual.note}
                        </p>
                      )}
                      {tier.footnote && (
                        <p className="mt-1.5 text-xs text-muted-foreground">
                          {tier.footnote}
                        </p>
                      )}
                    </div>

                    <ul className="flex-1 space-y-2 border-t border-hairline pt-5">
                      {tier.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm">
                          <Check
                            className={cn(
                              'mt-0.5 size-4 shrink-0',
                              tier.highlighted ? 'text-primary' : 'text-success',
                            )}
                          />
                          <span className="text-foreground/90">{f}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      variant={tier.cta.primary ? 'default' : 'outline'}
                      onClick={() => {
                        if (tier.name === 'Enterprise') {
                          window.location.href =
                            'mailto:sales@aicostr.com?subject=Enterprise%20enquiry';
                        } else {
                          navigate('/signup');
                        }
                      }}
                    >
                      {tier.cta.label}
                    </Button>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
